import React, { useEffect, useRef } from 'react';
import { X } from 'lucide-react';

/**
 * Composant Modal accessible (WCAG 2.1 Level AA)
 * 
 * Améliorations d'accessibilité :
 * - role="dialog" et aria-modal="true"
 * - aria-labelledby pour le titre
 * - aria-describedby pour la description
 * - Focus trap (focus reste dans le modal)
 * - Gestion de la touche Escape
 * - Restauration du focus après fermeture
 * - Annonce aux lecteurs d'écran
 */
const Modal = ({ 
  isOpen, 
  onClose, 
  title, 
  children, 
  size = 'md',
  showCloseButton = true,
  description, // 🆕 Description accessible
  closeOnEscape = true, // 🆕 Permettre de désactiver Escape
  closeOnOverlayClick = true, // 🆕 Permettre de désactiver clic overlay
  ...props
}) => {
  const modalRef = useRef(null);
  const previousFocusRef = useRef(null);
  const titleId = useRef(`modal-title-${Math.random().toString(36).substr(2, 9)}`).current;
  const descId = useRef(`modal-desc-${Math.random().toString(36).substr(2, 9)}`).current;

  // 🆕 Focus trap et gestion clavier
  useEffect(() => {
    if (!isOpen) return;

    // Sauvegarder l'élément qui avait le focus
    previousFocusRef.current = document.activeElement;

    // Bloquer le scroll du body
    document.body.style.overflow = 'hidden';

    // Attendre le prochain tick pour que le modal soit rendu
    const timer = setTimeout(() => {
      if (modalRef.current) {
        // Trouver tous les éléments focusables
        const focusableElements = modalRef.current.querySelectorAll(
          'button, [href], input, select, textarea, [tabindex]:not([tabindex="-1"])'
        );
        
        if (focusableElements.length > 0) {
          // Focus sur le premier élément focusable (généralement le bouton fermer)
          focusableElements[0].focus();
        } else {
          // Sinon focus sur le modal lui-même
          modalRef.current.focus();
        }
      }
    }, 0);

    // 🆕 Gestion de la touche Escape
    const handleEscape = (e) => {
      if (e.key === 'Escape' && closeOnEscape) {
        onClose();
      }
    };

    // 🆕 Focus trap : garder le focus dans le modal
    const handleTab = (e) => {
      if (e.key !== 'Tab' || !modalRef.current) return;

      const focusableElements = modalRef.current.querySelectorAll(
        'button, [href], input, select, textarea, [tabindex]:not([tabindex="-1"])'
      );

      if (focusableElements.length === 0) return;

      const firstElement = focusableElements[0];
      const lastElement = focusableElements[focusableElements.length - 1];

      // Shift + Tab sur le premier élément → aller au dernier
      if (e.shiftKey && document.activeElement === firstElement) {
        e.preventDefault();
        lastElement.focus();
      }
      // Tab sur le dernier élément → aller au premier
      else if (!e.shiftKey && document.activeElement === lastElement) {
        e.preventDefault();
        firstElement.focus();
      }
    };

    document.addEventListener('keydown', handleEscape);
    document.addEventListener('keydown', handleTab);

    return () => {
      document.removeEventListener('keydown', handleEscape);
      document.removeEventListener('keydown', handleTab);
      document.body.style.overflow = 'unset';
      
      // 🆕 Restaurer le focus à l'élément précédent
      if (previousFocusRef.current) {
        previousFocusRef.current.focus();
      }

      clearTimeout(timer);
    };
  }, [isOpen, onClose, closeOnEscape]);

  if (!isOpen) return null;

  const sizes = {
    sm: 'max-w-md',
    md: 'max-w-2xl',
    lg: 'max-w-4xl',
    xl: 'max-w-6xl'
  };

  const handleOverlayClick = () => {
    if (closeOnOverlayClick) {
      onClose();
    }
  };

  return (
    <div 
      className="fixed inset-0 bg-black bg-opacity-50 dark:bg-opacity-70 z-50 flex items-start justify-center overflow-y-auto"
      onClick={handleOverlayClick}
      style={{ paddingTop: '2rem', paddingBottom: '2rem' }}
      aria-hidden="false"
    >
      <div 
        ref={modalRef}
        role="dialog"
        aria-modal="true"
        aria-labelledby={title ? titleId : undefined}
        aria-describedby={description ? descId : undefined}
        tabIndex={-1}
        className={`bg-white dark:bg-gray-800 rounded-lg ${sizes[size]} w-full mx-4 shadow-xl`}
        onClick={(e) => e.stopPropagation()}
        style={{ maxHeight: 'calc(100vh - 4rem)' }}
        {...props}
      >
        {/* Header */}
        {(title || showCloseButton) && (
          <div className="flex justify-between items-center p-4 md:p-6 border-b dark:border-gray-700 bg-white dark:bg-gray-800 rounded-t-lg sticky top-0 z-10">
            {title && (
              <h2 
                id={titleId}
                className="text-xl md:text-2xl font-bold dark:text-white"
              >
                {title}
              </h2>
            )}
            {showCloseButton && (
              <button
                onClick={onClose}
                className="text-gray-400 hover:text-gray-600 dark:text-gray-500 dark:hover:text-gray-300 transition-colors focus:outline-none focus:ring-2 focus:ring-blue-500 rounded"
                aria-label="Fermer le dialogue"
              >
                <X size={24} aria-hidden="true" />
                <span className="sr-only">Fermer</span>
              </button>
            )}
          </div>
        )}
        
        {/* 🆕 Description cachée pour lecteurs d'écran */}
        {description && (
          <p id={descId} className="sr-only">
            {description}
          </p>
        )}
        
        {/* Content */}
        <div 
          className="p-4 md:p-6 overflow-y-auto" 
          style={{ maxHeight: 'calc(100vh - 12rem)' }}
        >
          {children}
        </div>
      </div>
    </div>
  );
};

export default Modal;
